import React, {
  Fragment,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import ExpandRow from '../../../../core/components/expand-row';
import TagGroupTitle from './tag-group-title';
import { textEllipse } from '../../../../core/utils/text-format-helper';

import {
  Container,
  Card,
  CardHeader,
  Text,
  Checkbox,
  Divider,
  RowContentContainer,
  CheckboxTag,
  IconLabel,
  FilterLabel,
} from './styles';

const FilterTags = (props) => {
  const {
    groupedTags,
    selectedTags,
    onChange,
    loading,
  } = props;

  /**
   * "grouped" is an object that has "selectAll", "tags" and "tagGroup" properties.
   *  @description:
   *  grouped = {selectAll: { isAllTagsSelected }, tags: {id, tagGroupId, checked}, tagGroup: { isTagsGroupSelected }}
   * "selectAll" is used to know when all tags are selected.
   * "tags" is used to know when a tag is selected.
   * "tagGroup" is used to know when all tags of it's group is selected.
   */
  const grouped = useMemo(() => {
    const group = { selectAll: { isAllTagsSelected: false }, tags: {}, tagGroup: {} };

    const tagsId = _.groupBy(selectedTags, (v) => v);

    let isAllTagsSelected = true;

    _.forEach(groupedTags, (e) => {
      group.tagGroup[e.id] = { isTagsGroupSelected: false };
      let isTagsGroupSelected = true;

      _.forEach(e.tags, (v) => {
        group.tags[v.id] = { id: v.id, tagGroupId: e.id, checked: !!tagsId[v.id] };

        if (!tagsId[v.id]) {
          isTagsGroupSelected = false;
          isAllTagsSelected = false;
        }
      });
      group.tagGroup[e.id].isTagsGroupSelected = isTagsGroupSelected && !_.isEmpty(e.tags);
      group.selectAll.isAllTagsSelected = isAllTagsSelected && !_.isEmpty(selectedTags);
    });
    return group;
  }, [groupedTags, selectedTags]);

  const handleSelected = (checked, value) => {
    if (checked) {
      onChange([
        ..._.chain(grouped.tags).filter('checked').map('id').value(),
        value,
      ]);
    } else {
      onChange(_.chain(grouped.tags).filter((v) => v.id !== value && v.checked).map('id').value());
    }
  };

  const handleSelectTagGroup = (groupTag, checked, tagGroupId) => {
    if (checked) {
      onChange([
        ..._.chain(grouped.tags).filter((tag) => tag.tagGroupId !== tagGroupId && tag.checked).map('id').value(),
        ..._.map(groupTag, 'id')]);
    } else {
      onChange(_.chain(grouped.tags).filter((tag) => tag.tagGroupId !== tagGroupId && tag.checked).map('id').value());
    }
  };

  const handleSelectAll = (checked) => {
    onChange(checked ? _.map(grouped.tags, 'id') : []);
  };

  return (
    <Container>

      <Card>
        <CardHeader>
          <Text as="h3">
            Filter Tags
          </Text>

          <Checkbox
            checked={grouped.selectAll.isAllTagsSelected}
            label="Select all"
            onChange={(e, { checked }) => { handleSelectAll(checked); }}
          />

        </CardHeader>

        {!loading && groupedTags && groupedTags.map((row) => {
          const { tags } = row;

          return (
            <Fragment key={row.id}>

              <Divider />

              <ExpandRow>
                <ExpandRow.RowTitle>
                  <TagGroupTitle
                    name={row.name}
                    countTagHighlight={row.countTagHighlight}
                    color={row.color}
                  />
                </ExpandRow.RowTitle>

                <ExpandRow.RowContent>
                  <RowContentContainer>
                    <CheckboxTag
                      checked={grouped.tagGroup[row.id].isTagsGroupSelected}
                      disabled={_.isEmpty(tags)}
                      label="Select All"
                      color={row.color}
                      onChange={(e, { checked }) => { handleSelectTagGroup(tags, checked, row.id); }}
                    />
                  </RowContentContainer>

                  {tags.map((tag) => {
                    const checkTag = grouped.tags[tag.id] || { checked: false };

                    return (
                      <RowContentContainer key={tag.id}>
                        <CheckboxTag
                          checked={checkTag.checked}
                          label={(
                            <FilterLabel>
                              {tag.icon && (
                                <IconLabel src={tag.icon} alt={tag.icon} />
                              )}
                              {`${textEllipse(tag.name, 28)} (${tag.count})`}
                            </FilterLabel>
                          )}
                          color={row.color}
                          onChange={(e, { checked }) => { handleSelected(checked, tag.id); }}
                        />
                      </RowContentContainer>
                    );
                  })}

                </ExpandRow.RowContent>
              </ExpandRow>
            </Fragment>
          );
        })}

      </Card>
    </Container>
  );
};

FilterTags.propTypes = {
  groupedTags: PropTypes.arrayOf(PropTypes.shape({
    color: PropTypes.string,
    description: PropTypes.string,
    id: PropTypes.string,
    name: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.shape({
      count: PropTypes.number,
      id: PropTypes.string,
      name: PropTypes.string,
    })),
  })).isRequired,

  selectedTags: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

FilterTags.defaultProps = {
  loading: false,
  selectedTags: [],
};

export default FilterTags;
